import React, { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx";
import ScrollToTop from "./components/ScrollToTop.tsx";
import ScrollToTopOnRouteChange from "./components/ScrollToTopOnRouteChange.tsx";
import ScrollProgressBar from "./components/ScrollProgressBar";
import CookieConsentBanner from "./components/CookieConsentBanner";
import ConsentAwareAnalytics from "./components/ConsentAwareAnalytics";
import FloatingTripQuiz from "./components/FloatingTripQuiz";

// Lazy-loaded route pages — keeps initial bundle small and page transitions snappy
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const DailyCruises = lazy(() => import("./pages/DailyCruises.tsx"));
const CruiseDetail = lazy(() => import("./pages/CruiseDetail.tsx"));
const BoatRentals = lazy(() => import("./pages/BoatRentals.tsx"));
const BoatRentalDetail = lazy(() => import("./pages/BoatRentalDetail.tsx"));
const OasisBoatParty = lazy(() => import("./pages/OasisBoatParty.tsx"));
const Accommodations = lazy(() => import("./pages/Accommodations.tsx"));
const AccommodationDetail = lazy(() => import("./pages/AccommodationDetail.tsx"));
const Villas = lazy(() => import("./pages/Villas.tsx"));
const BoutiqueHotels = lazy(() => import("./pages/BoutiqueHotels.tsx"));
const Apartments = lazy(() => import("./pages/Apartments.tsx"));
const Rentals = lazy(() => import("./pages/Rentals.tsx"));
const PrivateTransfers = lazy(() => import("./pages/PrivateTransfers.tsx"));
const Activities = lazy(() => import("./pages/Activities.tsx"));
const ActivityDetail = lazy(() => import("./pages/ActivityDetail.tsx"));
const Services = lazy(() => import("./pages/Services.tsx"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const AboutUs = lazy(() => import("./pages/AboutUs.tsx"));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
const BecomePartner = lazy(() => import("./pages/BecomePartner.tsx"));
const Islands = lazy(() => import("./pages/Islands.tsx"));
const SkiathosIsland = lazy(() => import("./pages/SkiathosIsland.tsx"));
const AccommodationBookingStatus = lazy(() => import("./pages/AccommodationBookingStatus.tsx"));
const PartnerResponse = lazy(() => import("./pages/PartnerResponse.tsx"));
const BoatBookingAlternatives = lazy(() => import("./pages/BoatBookingAlternatives.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy.tsx"));
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService.tsx"));
const CookiePolicy = lazy(() => import("./pages/legal/CookiePolicy.tsx"));
const CancellationPolicy = lazy(() => import("./pages/legal/CancellationPolicy.tsx"));
const SearchRecommendations = lazy(() => import("./pages/SearchRecommendations.tsx"));
const ReviewPage = lazy(() => import("./pages/ReviewPage.tsx"));

// Admin (lazy — heavy, rarely accessed by visitors)
const AdminLogin = lazy(() => import("./pages/AdminLogin.tsx"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout.tsx"));
const AdminDashboardPage = lazy(() => import("./pages/admin/AdminDashboardPage.tsx"));
const AdminServicesPage = lazy(() => import("./pages/admin/AdminServicesPage.tsx"));
const AdminBookingsPage = lazy(() => import("./pages/admin/AdminBookingsPage.tsx"));
const AdminPartnersPage = lazy(() => import("./pages/admin/AdminPartnersPage.tsx"));
const AdminInquiriesPage = lazy(() => import("./pages/admin/AdminInquiriesPage.tsx"));
const AdminAccommodationBookingsPage = lazy(() => import("./pages/admin/AdminAccommodationBookingsPage.tsx"));
const AdminActivityBookingsPage = lazy(() => import("./pages/admin/AdminActivityBookingsPage.tsx"));
const AdminTransfersPage = lazy(() => import("./pages/admin/AdminTransfersPage.tsx"));
const AdminTransferBookingsPage = lazy(() => import("./pages/admin/AdminTransferBookingsPage.tsx"));
const AdminVehicleRentalsPage = lazy(() => import("./pages/admin/AdminVehicleRentalsPage.tsx"));
const AdminBookingRequestsPage = lazy(() => import("./pages/admin/AdminBookingRequestsPage.tsx"));
const AdminBlogPage = lazy(() => import("./pages/admin/AdminBlogPage.tsx"));
const AdminCouponsPage = lazy(() => import("./pages/admin/AdminCouponsPage.tsx"));
const AdminCruiseBookingsPage = lazy(() => import("./pages/admin/AdminCruiseBookingsPage.tsx"));
const AdminBoatPartyBookingsPage = lazy(() => import("./pages/admin/AdminBoatPartyBookingsPage.tsx"));
const AdminGuidePage = lazy(() => import("./pages/admin/AdminGuidePage.tsx"));
const AdminOffersPage = lazy(() => import("./pages/admin/AdminOffersPage.tsx"));
const AdminReviewsPage = lazy(() => import("./pages/admin/AdminReviewsPage.tsx"));

// Partner (lazy)
const PartnerLogin = lazy(() => import("./pages/PartnerLogin.tsx"));
const PartnerLayout = lazy(() => import("./components/partner/PartnerLayout.tsx"));
const PartnerBoatsPage = lazy(() => import("./pages/partner/PartnerBoatsPage.tsx"));
const PartnerBookingsPage = lazy(() => import("./pages/partner/PartnerBookingsPage.tsx"));
const PartnerAccommodationsPage = lazy(() => import("./pages/partner/PartnerAccommodationsPage.tsx"));
const PartnerVehiclesPage = lazy(() => import("./pages/partner/PartnerVehiclesPage.tsx"));
const PartnerVehicleBookingsPage = lazy(() => import("./pages/partner/PartnerVehicleBookingsPage.tsx"));
const PartnerSettingsPage = lazy(() => import("./pages/partner/PartnerSettingsPage.tsx"));
const PartnerAccommodationBookingsPage = lazy(() => import("./pages/partner/PartnerAccommodationBookingsPage.tsx"));
const PartnerActivityBookingsPage = lazy(() => import("./pages/partner/PartnerActivityBookingsPage.tsx"));
const PartnerStatsPage = lazy(() => import("./pages/partner/PartnerStatsPage.tsx"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes — avoid refetching on quick re-navigation
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

// Minimal, brand-consistent fallback — no heavy spinner that would flash
const RouteFallback = () => (
  <div className="min-h-screen bg-background" aria-hidden="true" />
);

// Redirect /daily-cruises/:id → /skiathos-daily-cruises/:id preserving the param
const DailyCruiseDetailRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/skiathos-daily-cruises/${id}`} replace />;
};

// Redirect /boat-rentals/:id → /private-boat-rentals-in-skiathos/:id preserving the param
const BoatRentalDetailRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/private-boat-rentals-in-skiathos/${id}`} replace />;
};

// Strip trailing slashes from any URL (except root) and redirect to the canonical
// no-slash form. Mounted before <Routes> so /foo/ → /foo, /foo/bar/ → /foo/bar.
// Preserves search params and hash so deep links keep working.
const TrailingSlashRedirect = () => {
  const { pathname, search, hash } = useLocation();
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return <Navigate to={pathname.replace(/\/+$/, "") + search + hash} replace />;
  }
  return null;
};

class GlobalErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: any}> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      const isChunkLoadFailed = this.state.error?.message?.toLowerCase().includes('dynamically imported module') || 
                                this.state.error?.message?.toLowerCase().includes('failed to fetch');
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full bg-card p-6 rounded-2xl shadow-elevated border border-border text-center">
            <h2 className="text-xl font-bold font-display text-foreground mb-4">
              {isChunkLoadFailed ? "Υπάρχει Νέα Έκδοση!" : "Κάτι πήγε στραβά"}
            </h2>
            <p className="text-muted-foreground text-sm font-body mb-6">
              {isChunkLoadFailed 
                ? "Μια νέα έκδοση της πλατφόρμας είναι διαθέσιμη. Η παλιά έκδοση προσπάθησε να φορτώσει αρχεία που δεν υπάρχουν πια."
                : "Προέκυψε ένα σφάλμα κατά τη φόρτωση της σελίδας."}
            </p>
            {this.state.error && !isChunkLoadFailed && (
              <div className="bg-destructive/10 text-destructive text-xs p-3 rounded-lg text-left mb-6 font-mono overflow-auto max-h-32">
                {String(this.state.error)}
              </div>
            )}
            <button 
              onClick={() => window.location.reload()}
              className="bg-primary text-primary-foreground px-6 py-2.5 rounded-xl font-bold font-body w-full hover:bg-primary/90 transition-colors"
            >
              Ανανέωση Σελίδας
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <ScrollToTop />
      <ScrollProgressBar />
      <ConsentAwareAnalytics />
      <BrowserRouter>
        <ScrollToTopOnRouteChange />
        <TrailingSlashRedirect />
        <FloatingTripQuiz />
        <GlobalErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/skiathos-daily-cruises" element={<DailyCruises />} />
            <Route path="/skiathos-daily-cruises/:id" element={<CruiseDetail />} />
            <Route path="/daily-cruises" element={<Navigate to="/skiathos-daily-cruises" replace />} />
            <Route path="/daily-cruises/:id" element={<DailyCruiseDetailRedirect />} />

            <Route path="/private-boat-rentals-in-skiathos" element={<BoatRentals />} />
            <Route path="/private-boat-rentals-in-skiathos/:id" element={<BoatRentalDetail />} />
            <Route path="/boat-rentals" element={<Navigate to="/private-boat-rentals-in-skiathos" replace />} />
            <Route path="/boat-rentals/:id" element={<BoatRentalDetailRedirect />} />
            <Route path="/skiathos-boat-party" element={<OasisBoatParty />} />
            <Route path="/oasis-boat-party" element={<Navigate to="/skiathos-boat-party" replace />} />
            <Route path="/accommodations" element={<Accommodations />} />
            <Route path="/accommodations/:id" element={<AccommodationDetail />} />
            <Route path="/villas" element={<Villas />} />
            <Route path="/boutique-hotels" element={<BoutiqueHotels />} />
            <Route path="/apartments" element={<Apartments />} />
            <Route path="/rentals" element={<Rentals />} />
            <Route path="/private-transfers" element={<PrivateTransfers />} />
            <Route path="/activities" element={<Activities />} />
            <Route path="/activities/:id" element={<ActivityDetail />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:id" element={<ServiceDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            {/* Canonical dated blog URL — /YYYY/MM/DD/slug. BlogPost validates the date params. */}
            <Route path="/:year/:month/:day/:slug" element={<BlogPost />} />
            <Route path="/become-our-partner" element={<BecomePartner />} />
            <Route path="/islands" element={<Islands />} />
            <Route path="/islands/skiathos" element={<SkiathosIsland />} />
            <Route path="/booking-status/:token" element={<AccommodationBookingStatus />} />
            <Route path="/partner-respond/:token" element={<PartnerResponse />} />
            <Route path="/partner-respond/accommodation/:token" element={<PartnerResponse />} />
            <Route path="/partner-respond/vehicle/:token" element={<PartnerResponse />} />
            <Route path="/boat-booking/alternatives" element={<BoatBookingAlternatives />} />
            <Route path="/search" element={<SearchRecommendations />} />
            <Route path="/review" element={<ReviewPage />} />

            {/* Legal pages */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/cancellation-policy" element={<CancellationPolicy />} />

            {/* Admin routes */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route path="dashboard" element={<AdminDashboardPage />} />
              <Route path="bookings" element={<AdminBookingsPage />} />
              <Route path="accommodation-bookings" element={<AdminAccommodationBookingsPage />} />
              <Route path="activity-bookings" element={<AdminActivityBookingsPage />} />
              <Route path="partners" element={<AdminPartnersPage />} />

              <Route path="inquiries" element={<AdminInquiriesPage />} />
              <Route path="services/:category" element={<AdminServicesPage />} />
              <Route path="transfers" element={<AdminTransfersPage />} />
              <Route path="transfer-bookings" element={<AdminTransferBookingsPage />} />
              <Route path="vehicle-rentals" element={<AdminVehicleRentalsPage />} />
              <Route path="booking-requests" element={<AdminBookingRequestsPage />} />
              <Route path="blog" element={<AdminBlogPage />} />
              <Route path="coupons" element={<AdminCouponsPage />} />
              <Route path="cruise-bookings" element={<AdminCruiseBookingsPage />} />
              <Route path="boat-party-bookings" element={<AdminBoatPartyBookingsPage />} />
              <Route path="offers" element={<AdminOffersPage />} />
              <Route path="reviews" element={<AdminReviewsPage />} />
              <Route path="guide" element={<AdminGuidePage />} />
            </Route>

            {/* Partner routes */}
            <Route path="/partner" element={<PartnerLogin />} />
            <Route path="/partner" element={<PartnerLayout />}>
              <Route path="boats" element={<PartnerBoatsPage />} />
              <Route path="accommodations" element={<PartnerAccommodationsPage />} />
              <Route path="accommodation-bookings" element={<PartnerAccommodationBookingsPage />} />
              <Route path="vehicles" element={<PartnerVehiclesPage />} />
              <Route path="vehicle-requests" element={<PartnerVehicleBookingsPage />} />
              <Route path="activity-bookings" element={<PartnerActivityBookingsPage />} />
              <Route path="bookings" element={<PartnerBookingsPage />} />
              <Route path="stats" element={<PartnerStatsPage />} />
              <Route path="settings" element={<PartnerSettingsPage />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </GlobalErrorBoundary>
        <CookieConsentBanner />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
